<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import XlTag from '../xl/XlTag.vue';
import IconBack from '../icons/IconBack.vue';
import IconPlay from '../icons/IconPlay.vue';
import IconRepeat from '../icons/IconRepeat.vue';
import IconSparkle from '../icons/IconSparkle.vue';
import PortalJoinButton from './PortalJoinButton.vue';
import PortalSessionFiles from './PortalSessionFiles.vue';
import PortalSessionSpeakers from './PortalSessionSpeakers.vue';
import PortalSessionDates from './PortalSessionDates.vue';
import SpeakingStatePanel from '../views/SessionSpeaking/SpeakingStatePanel.vue';
import { mySession } from '../../api/portal';
import { viewerTimeZone } from '../../helpers/greeting';
import { writeSeat } from '../../helpers/sessionSeat';
import { speakingLook } from '../../helpers/speakerConsent';
import { minutesLabel } from '../../helpers/sessionCard';
import {
  clock, dayLong, kindLabel, longDay, seatSavedLine, seriesLabel, timeState, zoneName,
} from '../../helpers/sessionTimes';
import { reportError } from '../../faro';
import { useBackTo } from '../../composables/useBackTo';
import { useEventStore } from '../../stores/event';

/**
 * One session's page (XN-4558): the prototype's sdetail (build 30 Sep 12:40),
 * with the spec's states. Before it runs: what it is, the recording statement
 * before any decision, the seat (for the series, as the list does) or Join,
 * then who, what and the other dates. After: what became of it. The room is
 * hosted around this page, so Join opens the same call the list does.
 *
 * Somebody asked to speak gets the amber panel and the way into the accept
 * screen; somebody speaking gets the green one, with their consent record and
 * the way out. Both routes land on the same accept screen as the email link.
 */
const route = useRoute();
const router = useRouter();
const room = inject('portalRoom', null);
const eventStore = useEventStore();
const agency = computed(() => eventStore.event?.value?.login_event_name || 'your agency');
const timeZone = viewerTimeZone();

const state = ref('loading'); // loading | ready | missing | error
const session = ref(null);
const now = ref(new Date());
let tick = null;
const busy = ref(false);
const note = ref('');
const joining = ref(false);

function settleFailure(error, id, quiet) {
  if (route.params.id !== id) return;
  if (quiet) { reportError(error); return; }
  if (error?.response?.status === 404) { state.value = 'missing'; return; }
  reportError(error);
  state.value = 'error';
}

// Quiet is a look behind what's shown: no loading state, and a failure keeps the page.
async function load({ quiet = false } = {}) {
  const id = route.params.id;
  if (!quiet) {
    state.value = 'loading';
    note.value = '';
    busy.value = false;
  }
  try {
    const loaded = await mySession(id);
    if (route.params.id !== id) return; // a newer page has been asked for
    session.value = loaded;
    state.value = 'ready';
  } catch (error) {
    settleFailure(error, id, quiet);
  }
}
watch(() => route.params.id, () => { if (route.params.id) load(); });

// A reader who lands here (first arrival or a new session) starts at the title, not at <body>.
const heading = ref(null);
watch(state, async (value) => {
  if (value !== 'ready' && value !== 'missing') return;
  await nextTick();
  heading.value?.focus();
});

const time = computed(() => (session.value ? timeState(session.value, now.value) : {}));
const speak = computed(() => speakingLook(session.value?.speaking));
const openInvitation = () => router.push(session.value?.speaking?.invitation_path ?? `/hub/sessions/${session.value.id}/speaking`);
const cancelled = computed(() => session.value?.status === 'cancelled');
const ended = computed(() => !cancelled.value && time.value.ended);
const upcoming = computed(() => !cancelled.value && !time.value.ended);
const published = computed(() => session.value?.recording?.state === 'published');
// After it runs, what exists decides, not the switch (XN-4688): the page says when nothing was recorded.
const recordingExists = computed(() => ['published', 'preparing'].includes(session.value?.recording?.state));

const dateLine = computed(() => {
  const s = session.value;
  return [
    longDay(s.starts_at, timeZone),
    clock(s.starts_at, timeZone),
    minutesLabel(s.minutes),
    zoneName(timeZone, new Date(s.starts_at)),
    ended.value ? 'this session has ended' : null,
  ].filter(Boolean).join(' · ');
});

// The seat, as the list card holds it: shown at once, put back on failure.
async function toggleSeat() {
  if (busy.value) return;
  const s = session.value;
  const on = !s.seat;
  const before = { seat: s.seat, series: s.series };
  busy.value = true;
  note.value = '';
  setSeats(on, true);
  const outcome = await writeSeat(s.id, on, { series: Boolean(s.series) });
  if (session.value !== s) return; // the page moved on; load() reset busy and note
  busy.value = false;
  if (outcome === 'saved') note.value = seatSavedLine(s, now.value, timeZone);
  else if (outcome === 'released') note.value = 'Seat canceled.';
  else {
    s.seat = before.seat;
    s.series = before.series;
    if (outcome === 'gone') state.value = 'missing';
  }
}

// The page's button covers the series, so every date follows it.
function setSeats(on, wholeSeries) {
  const s = session.value;
  s.seat = on;
  if (wholeSeries && s.series?.dates) s.series = { ...s.series, dates: s.series.dates.map((d) => ({ ...d, seat: on })) };
}

// One date's toggle: when it is this page's date, the page's seat follows it.
function onDateSeat({ id, seat }) {
  const s = session.value;
  s.series = { ...s.series, dates: s.series.dates.map((d) => (d.id === id ? { ...d, seat } : d)) };
  if (id === s.id) s.seat = seat;
  // The page's own line spoke for the series; once one date changes, the date's own note speaks for it.
  note.value = '';
}
function onDateGone(id) {
  if (id === session.value.id) { state.value = 'missing'; return; }
  const s = session.value;
  s.series = { ...s.series, dates: s.series.dates.filter((d) => d.id !== id) };
}

// Join is busy while its own room.join() is pending.
async function join() {
  joining.value = true;
  try {
    await room?.join(session.value.id);
  } finally {
    joining.value = false;
  }
}

// Back to wherever they came from: the list, Home's Coming up, a reminder email.
const { label: backLabel, go: goBack } = useBackTo({ to: '/hub/sessions', label: 'All sessions', list: /^\/hub\/sessions(\?|$)/ });

onMounted(() => {
  load();
  // The server answers "not recorded" until a session has ended: when it ends under an open page, ask again.
  tick = setInterval(() => {
    const before = now.value;
    now.value = new Date();
    const s = session.value;
    if (s && !cancelled.value && !timeState(s, before).ended && timeState(s, now.value).ended) load({ quiet: true });
  }, 60000);
});
onBeforeUnmount(() => clearInterval(tick));
</script>

<template>
  <section class="session">
    <XlButton variant="ghost" size="sm" class="session__back" data-testid="session-back" @click="goBack">
      <template #icon><IconBack color="currentColor" width="15" height="15" /></template>
      {{ backLabel }}
    </XlButton>

    <div v-if="state === 'loading'" class="session__loading" aria-busy="true">
      <XlSkeleton height="38px" width="60%" />
      <XlSkeleton variant="text" :lines="3" />
    </div>

    <div v-else-if="state === 'error'" class="session__error" role="alert">
      <p>This session couldn't be loaded.</p>
      <XlButton variant="ghost" size="sm" data-testid="session-retry" @click="load">Try again</XlButton>
    </div>

    <div v-else-if="state === 'missing'" class="session__missing">
      <h1 ref="heading" tabindex="-1" class="session__title">This session isn't available</h1>
      <RouterLink to="/hub/sessions" class="session__link">See what's coming up</RouterLink>
    </div>

    <template v-else>
      <h1 ref="heading" tabindex="-1" class="session__title">{{ session.title }}</h1>
      <p class="session__when">{{ dateLine }}</p>
      <div class="session__tags">
        <XlTag v-if="kindLabel(session.kind)" tone="brand">{{ kindLabel(session.kind) }}</XlTag>
        <XlTag v-if="session.series"><IconRepeat color="currentColor" size="13" /> {{ seriesLabel(session.series) }}</XlTag>
        <XlTag v-if="time.live" tone="success">Live now</XlTag>
        <XlTag v-if="speak && !cancelled" :tone="speak.tone" data-testid="session-speaking-tag">{{ speak.label }}</XlTag>
        <XlTag v-if="upcoming">{{ session.recorded ? 'Recorded' : 'Not recorded' }}</XlTag>
        <XlTag v-else-if="ended && recordingExists">Recorded</XlTag>
        <XlTag v-if="upcoming && session.seat" tone="success">Seat saved</XlTag>
      </div>

      <SpeakingStatePanel
        v-if="!cancelled && session.speaking"
        class="session__speaking"
        :speaking="session.speaking"
        :speaker-sees="session.speaking.speaker_sees || ''"
        @open="openInvitation"
        @see-notice="openInvitation"
      />

      <p v-if="cancelled" class="session__state" data-testid="session-cancelled">
        This session was canceled on {{ dayLong(session.cancelled_at, timeZone) }}.
        <RouterLink to="/hub/sessions" class="session__link">See what's coming up</RouterLink>
      </p>

      <template v-else-if="upcoming">
        <p class="session__statement">
          {{ session.recorded
            ? 'This session is recorded and transcribed. Your camera and your name are not published — only what is said.'
            : 'This session isn\'t recorded.' }}
        </p>
        <div class="session__actions">
          <XlButton
            v-if="!time.joinable && !speak"
            :variant="session.seat ? 'ghost' : 'filled'"
            data-testid="session-seat"
            @click="toggleSeat"
          >{{ session.seat ? 'Seat saved' : 'Save my seat' }}<span class="session__sr">: {{ session.title }}</span></XlButton>
          <!-- XN-4558: Join is always here, dimmed until 15 minutes before. -->
          <PortalJoinButton
            v-if="!speak?.asked"
            size="md"
            :speaker="Boolean(speak?.speaking)"
            :label="speak?.speaking ? 'Join as a speaker' : 'Join session'"
            :title="session.title"
            :joinable="time.joinable"
            :loading="joining"
            @join="join"
          />
          <p class="session__note" aria-live="polite" data-testid="session-note">{{ note }}</p>
        </div>
      </template>

      <!-- Published: the way to the recording ends the page instead (XN-4688, Greg's frame 1927:9007). -->
      <div v-else-if="ended && !published" class="session__after" data-testid="session-after">
        <p v-if="session.recording?.state === 'preparing'" class="session__state">
          The recording is being prepared. It'll appear here once {{ agency }} has checked it.
        </p>
        <p v-else class="session__state">
          This session has been and gone. It wasn't recorded.
          <RouterLink to="/hub/sessions" class="session__link">See what's coming up</RouterLink>
        </p>
      </div>

      <p v-if="session.description" class="session__about">{{ session.description }}</p>

      <PortalSessionSpeakers class="session__block" :host="session.host" :speakers="session.speakers ?? []" :level="2" />

      <section v-if="session.points?.length" class="session__block">
        <h2 class="session__h">What's covered</h2>
        <ul class="session__list"><li v-for="point in session.points" :key="point" data-testid="point">{{ point }}</li></ul>
      </section>

      <!-- The recap itself lives on the recording page; this one ends with the way there. -->
      <RouterLink v-if="ended && published" :to="`/hub/recordings/${session.id}`" class="session__watch" data-testid="session-cta">
        <IconSparkle v-if="session.recording.video === false" :size="18" />
        <IconPlay v-else color="currentColor" size="18" aria-hidden="true" />
        {{ session.recording.video === false ? 'Read the recap' : 'Watch the recording' }}
      </RouterLink>

      <PortalSessionFiles class="session__block" :files="session.handouts ?? []" :level="2" @emptied="heading?.focus()" />

      <PortalSessionDates
        v-if="session.series?.dates?.length"
        :series="session.series"
        :time-zone="timeZone"
        @seat="onDateSeat"
        @gone="onDateGone"
      />
    </template>
  </section>
</template>

<style scoped>
/* Prototype sdetail (build 30 Sep 12:40): h1 31px, date line 16.5px, tags, about 16px/62ch. The blocks under it are the panel's (build 15 Oct). */
.session__back { margin-bottom: 18px; }
.session__title { font-size: 31px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.session__when { font-size: 16.5px; color: var(--xl-color-secondary-text); margin: 0 0 6px; }
.session__tags { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 20px; }
.session__statement { margin: 0 0 14px; font-size: 14.5px; color: var(--xl-color-secondary-text); max-width: 62ch; }
.session__actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; }
.session__note { margin: 0; font-size: 13px; color: var(--xl-color-secondary-text); }
.session__about { font-size: 16px; line-height: 1.65; color: var(--xl-color-secondary-text); margin: 0 0 24px; max-width: 62ch; white-space: pre-line; }
.session__state { font-size: 15px; color: var(--xl-color-secondary-text); max-width: 62ch; }
.session__link { font-weight: 600; color: var(--xl-color-primary); margin-left: 6px; }
.session__missing .session__link { margin-left: 0; }
.session__loading { display: grid; gap: 14px; }
.session__watch {
  display: inline-flex; align-items: center; gap: 8px; height: 42px; margin-top: 22px; padding: 0 20px; border-radius: 99px;
  /* A link, so XlButton's filled look rather than XlButton itself. */
  background: linear-gradient(155deg, var(--xl-color-surface-tint), var(--xl-color-primary));
  box-shadow: 0 5px 16px rgba(94, 47, 120, .26);
  color: var(--xl-color-on-primary); font-size: 14px; font-weight: 600; text-decoration: none;
}
/* The panel's labels (prototype cpDetails, build 15 Oct), as Speakers and Files have them. */
.session__h { margin: 0 0 8px; font-size: 13px; font-weight: 700; color: var(--xl-color-on-surface); }
.session__block { margin-top: 34px; }
.session__speaking { margin-bottom: 20px; }
.session__list { margin: 0; padding-left: 20px; font-size: 14.5px; line-height: 1.8; color: var(--xl-color-on-surface-variant); }
.session__error {
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  padding: 18px 20px; border-radius: 16px; font-size: 14.5px;
  background: var(--xl-color-error-container, #FBEAEA); border: 1px solid var(--xl-color-error); color: var(--xl-color-error);
}
.session__error p { margin: 0; }
.session__back svg { width: 15px; height: 15px; }
.session__sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
@media (max-width: 600px) { .session__title { font-size: 26px; } }
</style>
